/**
 * Feature E07-F06 — Publish & Export typed api-client + Query hooks (web side).
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalises the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` (research-publish.ts) so the SPA speaks the
 * exact same shapes the Fastify route validates.
 *
 * Endpoints (routes/research-publish.ts, mounted under /research):
 *   GET  /research/:id/publish   → useResearchPublishSummary()  (pre-publish counts)
 *   POST /research/:id/publish   → useResearchPublish()         (export + email + library)
 *   GET  /research/:id/publish/xlsx → downloadPublishXlsx()     (EER-column .xlsx file, T08)
 *
 * Each response is validated against the shared schema at the boundary so a drifted
 * shape fails loudly rather than rendering garbage into the success dialog.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  publishSummaryResponseSchema,
  publishResultSchema,
  publishXlsxPath,
  PUBLISH_XLSX_MIME_TYPE,
  type PublishSummaryResponse,
  type PublishResult,
  type PublishOptions,
  type PublishSourcerEmail,
} from '@eer/shared';
import { API_BASE_URL, apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { getSessionToken } from '../api/session.js';

/** Feature-local query key (kept out of the shared registry). */
export const researchPublishKeys = {
  all: ['research', 'publish'] as const,
  summary: (requestId: number) => ['research', 'publish', 'summary', requestId] as const,
} as const;

/** GET /research/:id/publish — the pre-publish summary (counts + gate flag). */
export async function fetchPublishSummary(requestId: number): Promise<PublishSummaryResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/publish`, {
    command: 'research_publish',
  });
  return publishSummaryResponseSchema.parse(unwrap(result));
}

/** POST /research/:id/publish — run the publish (Sheets / email / library blocks). */
export async function postPublish(
  requestId: number,
  options: Partial<PublishOptions>,
  sourcerEmail?: PublishSourcerEmail,
): Promise<PublishResult> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/publish`,
    // E07-F06-T10 — `sourcerEmail` only when that block is requested (absent ⇒
    // no sourcer mail; keeps the T07 "no side effect by default" body).
    sourcerEmail ? { options, sourcerEmail } : { options },
    { command: 'research_publish' },
  );
  return publishResultSchema.parse(unwrap(result));
}

/**
 * GET /research/:id/publish/xlsx — E07-F06-T08: download the accepted long-list
 * as an .xlsx file (EER researcher columns). A binary body, so it bypasses the
 * JSON `apiClient` but sends the same Bearer session token. Resolves to the file
 * name on success; throws an Error carrying the server's message otherwise.
 */
export async function downloadPublishXlsx(requestId: number): Promise<string> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    Accept: `${PUBLISH_XLSX_MIME_TYPE}, application/json`,
    'X-EER-Command': 'research_publish_xlsx',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}${publishXlsxPath(requestId)}`, { headers });
  if (!res.ok) {
    let message = `Download failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as {
        error?: { message?: unknown } | string;
        message?: unknown;
      };
      const m =
        typeof body.error === 'object' && body.error
          ? body.error.message
          : (body.message ?? body.error);
      if (typeof m === 'string' && m) message = m;
    } catch {
      /* non-JSON error body — keep the HTTP status message */
    }
    throw new Error(message);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `research-${requestId}.xlsx`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  return fileName;
}

/** Pre-publish summary query — drives the Publish screen header counts + warning. */
export function useResearchPublishSummary(
  requestId: number,
  enabled: boolean = true,
): UseQueryResult<PublishSummaryResponse, ApiResultError> {
  return useQuery<PublishSummaryResponse, ApiResultError>({
    queryKey: researchPublishKeys.summary(requestId),
    queryFn: () => fetchPublishSummary(requestId),
    enabled: enabled && requestId > 0,
  });
}

/**
 * Publish mutation. On success the publish + research domains are invalidated so
 * History/Library reflect the new `status='published'` + sheet_url; the success
 * dialog reads the returned `PublishResult` (sheet url / recruiter / project).
 */
/** The publish mutation input: the three checkboxes + the optional sourcer email (T10). */
export interface PublishMutationInput {
  options: Partial<PublishOptions>;
  sourcerEmail?: PublishSourcerEmail;
}

export function useResearchPublish(
  requestId: number,
): UseMutationResult<PublishResult, ApiResultError, PublishMutationInput> {
  const queryClient = useQueryClient();
  return useMutation<PublishResult, ApiResultError, PublishMutationInput>({
    mutationFn: ({ options, sourcerEmail }) => postPublish(requestId, options, sourcerEmail),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: researchPublishKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['research'] });
    },
  });
}
